import { Box, Button, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useQuery } from '@tanstack/react-query';
import { Slot } from 'fhir/r4b';
import { DateTime } from 'luxon';
import { ReactNode, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { getLocations } from 'src/api/api';
import { useApiClients } from 'src/hooks/useAppClients';
import { BOOKING_CONFIG, ServiceCategoryCode } from 'utils/lib/ottehr-config/booking';
import { ScheduleType } from 'utils/lib/types/common';
import { nextAvailableFrom } from 'utils/lib/utils/scheduleUtils';
import { Slots, SlotsLoader } from './Slots';
interface TabPanelProps {
  children?: ReactNode;
  dir?: string;
  index: number;
  value: number;
}

const DATETIME_FULL_NO_YEAR = 'MMMM d, h:mm a ZZZZ';
const DATE_FULL_NO_YEAR = 'EEEE, MMMM d';
function createLocalDateTime(dateTime: DateTime | undefined, timezone: string): DateTime | undefined {
  let localDateTime: DateTime | undefined;
  if (dateTime !== undefined) {
    localDateTime = dateTime.setZone(timezone);
  }
  return localDateTime;
}

const TabPanel = (props: TabPanelProps): JSX.Element => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`appointment-picker-tabpanel-${index}`}
      aria-labelledby={`appointment-picker-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3, pb: 3 }}>{children}</Box>}
    </div>
  );
};

const tabProps = (
  index: number
): {
  id: string;
  'aria-controls': string;
} => {
  return {
    id: `appointment-picker-tab-${index}`,
    'aria-controls': `appointment-picker-tabpanel-${index}`,
  };
};
interface SlotPickerProps {
  slotData: Slot[] | undefined;
  slotsLoading: boolean;
  /** The slug + schedule type of the bookable target (Location, Group, or PR-direct). */
  bookableSlug?: string;
  bookableScheduleType?: ScheduleType;
  atLocationSlug?: string;
  /** Service category (required for group bookings; inferred or omitted otherwise). */
  serviceCategoryCode?: ServiceCategoryCode;
  slotListKey: 'available' | 'telemedAvailable';
  showPastSlots: boolean;
  timezone: string;
  selectedSlot: Slot | undefined;
  setSelectedSlot: (slot: Slot | undefined) => void;
}

const SlotPicker = ({
  slotData,
  slotsLoading,
  bookableSlug,
  bookableScheduleType = ScheduleType.location,
  atLocationSlug,
  serviceCategoryCode,
  slotListKey,
  showPastSlots,
  timezone,
  selectedSlot,
  setSelectedSlot,
}: SlotPickerProps): JSX.Element => {
  const { oystehrZambda } = useApiClients();
  const theme = useTheme();
  // "Other dates" calendar range in months, from the per-project booking config;
  // defaults to 1 month when unset (beam sets 12). The tab is always shown here —
  // slots for a picked day are fetched on demand (handleSelectOtherDate).
  const prebookMonthsAhead = BOOKING_CONFIG.prebookMaxMonthsAhead ?? 1;
  const [currentTab, setCurrentTab] = useState(0);
  const [nextDay, setNextDay] = useState<boolean>(false);
  const [otherDateSlots, setOtherDateSlots] = useState<Slot[]>([]);
  const [otherDateSlotsLoading, setOtherDateSlotsLoading] = useState(false);
  const [selectedOtherDate, setSelectedOtherDate] = useState<DateTime | undefined>();

  const visibleOtherDate =
    !showPastSlots && selectedOtherDate && selectedOtherDate <= DateTime.now().endOf('day')
      ? undefined
      : selectedOtherDate;

  const [slotsList, daySlotsMap] = useMemo(() => {
    if (slotData) {
      const slots = [...slotData];

      // This maps days to an array of slots
      const map: { [ord: number]: Slot[] } = slots.reduce(
        (accumulator, current) => {
          const dateOfCurrent = DateTime.fromISO(current.start, { zone: timezone });
          const existing = accumulator[dateOfCurrent.ordinal];
          if (existing) {
            existing.push(current);
          } else {
            accumulator[dateOfCurrent.ordinal] = [current];
          }
          return accumulator;
        },
        {} as { [ord: number]: Slot[] }
      );

      return [slots, map];
    }
    return [[], {}];
  }, [timezone, slotData]);

  const { firstAvailableDay, secondAvailableDay } = useMemo(() => {
    let firstAvailableDay: DateTime | undefined = undefined;
    let secondAvailableDay: DateTime | undefined = undefined;
    const currentTime = DateTime.now().setZone(timezone);
    if (slotsList == null || slotsList.length === 0) {
      return { firstAvailableDay, secondAvailableDay, lastSlot: undefined };
    }

    firstAvailableDay = createLocalDateTime(DateTime.fromISO(slotsList[0].start), timezone);
    const firstSlot = slotsList[0];
    const firstTime = DateTime.fromISO(firstSlot.start)?.setZone(timezone).toISODate();
    const currentExistingTime = currentTime?.setZone(timezone)?.toISODate();
    if (!firstTime || !currentExistingTime) {
      return { firstAvailableDay, secondAvailableDay, lastSlot: undefined };
    } else if (firstTime > currentExistingTime) {
      setNextDay(true);
    }
    if (firstAvailableDay) {
      secondAvailableDay = nextAvailableFrom(firstAvailableDay, slotsList, timezone);
      if (secondAvailableDay) {
        setNextDay(false);
      }
    }
    return { firstAvailableDay, secondAvailableDay };
  }, [slotsList, timezone]);

  const handleChangeTab = useCallback((_: SyntheticEvent, newTab: number) => {
    setCurrentTab(newTab);
    if (newTab >= 2) setSelectedOtherDate(undefined); // reset the selected date
  }, []);

  const selectedDate = useMemo(() => {
    if (currentTab === 0 && firstAvailableDay) {
      return firstAvailableDay;
    } else if (secondAvailableDay && currentTab === 1) {
      return secondAvailableDay;
    } else {
      if (visibleOtherDate) {
        return visibleOtherDate;
      }
      return firstAvailableDay;
    }
  }, [currentTab, firstAvailableDay, secondAvailableDay, visibleOtherDate]);

  const getSlotsForDate = useCallback(
    (date: DateTime | undefined): Slot[] => {
      if (date === undefined) {
        return [];
      }
      return daySlotsMap[date.ordinal] ?? [];
    },
    [daySlotsMap]
  );

  const isFirstAppointment = selectedSlot === slotsList[0];
  const slotsExist = getSlotsForDate(firstAvailableDay).length > 0 || getSlotsForDate(secondAvailableDay).length > 0;

  const handleSelectOtherDate = useCallback(
    async (newDate: DateTime | null) => {
      if (!newDate) return;
      setSelectedOtherDate(newDate);

      try {
        setOtherDateSlotsLoading(true);

        if (!bookableSlug || !oystehrZambda) return;

        const response = await getLocations(oystehrZambda, {
          slug: bookableSlug,
          scheduleType: bookableScheduleType,
          selectedDate: newDate.toISODate() ?? undefined,
          ...(serviceCategoryCode ? { serviceCategoryCode } : {}),
          ...(atLocationSlug ? { atLocationSlug } : {}),
        });

        setOtherDateSlots(response.available?.map((s) => s.slot) ?? []);
      } catch (error) {
        console.error('Error loading slots for date:', error);
      } finally {
        setOtherDateSlotsLoading(false);
      }
    },
    [oystehrZambda, bookableSlug, bookableScheduleType, serviceCategoryCode, atLocationSlug]
  );

  const isFirstAvailableDayToday = firstAvailableDay?.hasSame(DateTime.now().setZone(timezone), 'day') ?? false;

  const { data: todayPastSlots = [], isLoading: todayPastSlotsLoading } = useQuery({
    queryKey: [
      'today-past-slots',
      bookableSlug,
      bookableScheduleType,
      serviceCategoryCode,
      atLocationSlug,
      slotListKey,
      firstAvailableDay?.toISO(),
    ],
    queryFn: async () => {
      const response = await getLocations(oystehrZambda!, {
        slug: bookableSlug!,
        scheduleType: bookableScheduleType,
        selectedDate: firstAvailableDay!.toISODate() ?? undefined,
        ...(serviceCategoryCode ? { serviceCategoryCode } : {}),
        ...(atLocationSlug ? { atLocationSlug } : {}),
      });
      return (response[slotListKey] ?? [])
        .map((s) => s.slot)
        .filter((slot) => DateTime.fromISO(slot.start) < firstAvailableDay!);
    },
    enabled: showPastSlots && isFirstAvailableDayToday && Boolean(oystehrZambda) && Boolean(bookableSlug),
  });

  const otherDatesContent = (
    <>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          disableHighlightToday={true}
          views={['month', 'day']}
          value={visibleOtherDate ?? null}
          onChange={handleSelectOtherDate}
          shouldDisableDate={(date) => {
            const today = DateTime.now().startOf('day');
            const tomorrow = today.plus({ days: 1 });
            if (!showPastSlots) return date <= tomorrow.endOf('day');
            const firstDayCoveredByTabs = isFirstAvailableDayToday ? today : tomorrow;
            return date >= firstDayCoveredByTabs && date <= tomorrow.endOf('day');
          }}
          // Minus one day for timezone shenanigans
          minDate={showPastSlots ? undefined : firstAvailableDay?.minus({ days: 1 })}
          // Allow booking up to prebookMonthsAhead out (default 1).
          // Slots for the picked day are fetched on demand.
          maxDate={
            firstAvailableDay ? firstAvailableDay.plus({ months: prebookMonthsAhead }) : DateTime.now().endOf('day')
          }
        />
      </LocalizationProvider>
      {visibleOtherDate && (
        <>
          <Typography
            variant="h3"
            color="#000000"
            sx={{ textAlign: 'center', fontSize: '20px', color: theme.palette.primary.main, mt: 3 }}
          >
            {visibleOtherDate.toLocaleString(DateTime.DATE_HUGE)}
          </Typography>
          <Slots
            slots={otherDateSlots}
            timezone={timezone}
            selectedSlot={selectedSlot}
            setSelectedSlot={setSelectedSlot}
            loading={otherDateSlotsLoading}
          />
        </>
      )}
    </>
  );

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, padding: 2, borderRadius: 2, marginTop: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
        }}
      >
        <Typography variant="h3" color="primary" fontSize="20px">
          Select check-in date and time
        </Typography>
        {selectedDate?.offsetNameShort && (
          <Typography color="theme.palette.text.secondary" sx={{ pt: { xs: 1.5, md: 0.5 } }}>
            Time Zone: {selectedDate.setLocale('en-us')?.offsetNameShort}
          </Typography>
        )}
      </Box>
      {slotsList && selectedDate != undefined && slotsExist ? (
        <Button
          variant={isFirstAppointment ? 'contained' : 'outlined'}
          sx={{
            color: isFirstAppointment ? theme.palette.primary.contrastText : theme.palette.text.primary,
            border: isFirstAppointment
              ? `1px solid ${theme.palette.primary.main}`
              : `1px solid ${theme.palette.divider}`,
            p: 1,
            borderRadius: '8px',
            textAlign: 'center',
            mt: 2.5,
            mb: 1.5,
            width: '100%',
            textTransform: 'none',
            fontWeight: 400,
            display: { xs: 'block', md: 'inline' },
          }}
          onClick={() => setSelectedSlot(slotsList[0])}
          type="button"
          className="first-button"
        >
          <span style={{ fontWeight: 500 }}>First available time:&nbsp;</span>
          {firstAvailableDay?.toFormat(DATETIME_FULL_NO_YEAR)}
        </Button>
      ) : (
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            p: 1,
            borderRadius: '8px',
            textAlign: 'center',
            mt: 1,
            display: slotsExist ? 'inherit' : 'none',
          }}
        >
          <Typography variant="body2">Calculating...</Typography>
        </Box>
      )}
      {!slotsLoading ? (
        <>
          {firstAvailableDay ? (
            <Box>
              <Box sx={{ width: '100%' }}>
                <Tabs
                  value={currentTab}
                  onChange={handleChangeTab}
                  TabIndicatorProps={{
                    style: {
                      // background: otherColors.borderLightBlue,
                      background: theme.palette.secondary.main,
                      height: '5px',
                      borderRadius: '2.5px',
                    },
                  }}
                  variant="fullWidth"
                  aria-label="Appointment tabs for switching between appointments slots for today and tomorrow"
                >
                  <Tab
                    label={nextDay ? 'Tomorrow' : 'Today'}
                    {...tabProps(0)}
                    sx={{
                      color: currentTab == 0 ? theme.palette.secondary.main : theme.palette.text.secondary,
                      opacity: 1,
                      textTransform: 'capitalize',
                      fontWeight: 500,
                    }}
                  />
                  {secondAvailableDay && (
                    <Tab
                      label="Tomorrow"
                      {...tabProps(1)}
                      sx={{
                        color: currentTab == 1 ? theme.palette.secondary.main : theme.palette.text.secondary,
                        opacity: 1,
                        textTransform: 'capitalize',
                        fontWeight: 500,
                      }}
                    />
                  )}
                  <Tab
                    label="Other dates"
                    {...tabProps(secondAvailableDay ? 2 : 1)}
                    sx={{
                      color: currentTab === 2 ? theme.palette.secondary.main : theme.palette.text.secondary,
                      opacity: 1,
                      textTransform: 'capitalize',
                      fontWeight: 500,
                    }}
                  />
                </Tabs>
              </Box>
              <Box>
                <TabPanel value={currentTab} index={0} dir={theme.direction}>
                  <Typography
                    variant="h3"
                    color="#000000"
                    sx={{ textAlign: 'center', fontSize: '20px', color: theme.palette.primary.main }}
                  >
                    {firstAvailableDay?.toFormat(DATE_FULL_NO_YEAR)}
                  </Typography>
                  {showPastSlots && todayPastSlotsLoading && <SlotsLoader />}
                  <Slots
                    slots={
                      showPastSlots
                        ? [...todayPastSlots, ...getSlotsForDate(firstAvailableDay)]
                        : getSlotsForDate(firstAvailableDay).filter(
                            (slot) => DateTime.fromISO(slot.start) >= DateTime.now()
                          )
                    }
                    timezone={timezone}
                    selectedSlot={selectedSlot}
                    setSelectedSlot={setSelectedSlot}
                  />
                </TabPanel>
                <TabPanel value={currentTab} index={1} dir={theme.direction}>
                  <Typography
                    variant="h3"
                    color="#000000"
                    sx={{ textAlign: 'center', fontSize: '20px', color: theme.palette.primary.main }}
                  >
                    {secondAvailableDay?.toFormat(DATE_FULL_NO_YEAR)}
                  </Typography>
                  <Slots
                    slots={getSlotsForDate(secondAvailableDay)}
                    timezone={timezone}
                    selectedSlot={selectedSlot}
                    setSelectedSlot={setSelectedSlot}
                  />
                </TabPanel>
                <TabPanel value={currentTab} index={secondAvailableDay ? 2 : 1} dir={theme.direction}>
                  {otherDatesContent}
                </TabPanel>
              </Box>
            </Box>
          ) : showPastSlots ? (
            <Box sx={{ pt: 3, pb: 3 }}>{otherDatesContent}</Box>
          ) : (
            <Typography variant="body2" color="#000000" sx={{ textAlign: 'center', marginTop: '30px' }}>
              There are no slots available, please come in when we are open.
            </Typography>
          )}
        </>
      ) : (
        <SlotsLoader />
      )}
    </Box>
  );
};

export default SlotPicker;
